import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CircularProgress,
  Alert,
  Box,
  Typography,
  Button,
} from "@mui/material"; // For loading/error

// API Functions
import { getProductById, getProducts } from "../api/productApi";

// Components
import ProductTabs from "../components/ProductTabs";
import ImageMagnifier from "../components/ImageMagnifier";
import Accordion from "../components/Accordion";
import ImageGallery from "../components/ImageGallery";
import TechnicalSpecifications from "../components/TechnicalSpecifications";
import MainScrollText from "../components/MainScrollText";
import ProductSlider from "../components/ProductSlider";
import ContactForm from "../components/ContactForm";
// Removed: import useStore from "../context/store";

function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [relativeProducts, setRelativeProducts] = useState([]);
  const [currentSpec, setCurrentSpec] = useState(null); // Initialize as null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) {
      setError("Product ID is missing.");
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      setLoading(true);
      setError(null);
      setProduct(null); // Reset previous product
      setRelativeProducts([]);

      try {
        // Fetch the specific product
        const productResponse = await getProductById(productId);
        const fetchedProduct = productResponse.data;

        if (!fetchedProduct) {
          throw new Error("Product not found.");
        }

        setProduct(fetchedProduct);
        setCurrentSpec(fetchedProduct.specificationsDefault); // Set initial spec

        // Fetch all products to find related ones (Consider optimizing this on backend later)
        try {
          const allProductsResponse = await getProducts();
          const related = allProductsResponse.data.filter(
            (item) =>
              item.sector === fetchedProduct.sector &&
              item._id !== fetchedProduct._id // Exclude self
          );
          setRelativeProducts(related);
        } catch (allProductsError) {
          console.error("Error fetching related products:", allProductsError);
          // Continue even if related products fail to load
          setRelativeProducts([]);
        }
      } catch (err) {
        console.error("Fetch Product Detail Error:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch product details."
        );
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]); // Re-run effect if productId changes

  // --- Render Loading State ---
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // --- Render Error State ---
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/products" sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Box>
    );
  }

  // --- Render Not Found State ---
  if (!product) {
    // This state might be brief if loading finishes before product is set,
    // but good practice to handle it explicitly after loading/error checks
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Product not found.</Typography>
        <Button component={Link} to="/products" sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Box>
    );
  }

  // --- Render Product Details ---
  return (
    <div>
      {/* --- Banner Section --- */}
      <div
        style={{ backgroundImage: `url(${product.bannerImageUrl})` }}
        className=" bg-cover bg-center background-no-repeat background-blend-multiply pt-24 md:pt-48 pb-14 md:pb-28 px-4 md:px-0"
      >
        <div className="max-w-[1400px] mx-auto flex  h-full flex-col ">
          <div className="products-page">
            <h1 className="text-white md:text-8xl text-4xl text-left font-medium">
              {product.title}
            </h1>
          </div>
          <div className="filters text-left space-y-5 mt-10 max-w-4xl">
            <h2 className="text-white md:text-2xl md:pb-14">
              {product.hightLight_text}
            </h2>
            <div className="flex space-x-4 justify-end md:justify-start">
              <Link to="/contact">
                <button className="bg-[#cc673d] text-white border-[#cc673d]">
                  Make An Enquiry
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* --- ProductTabs --- */}
      <ProductTabs />

      {/* --- Overview Section --- */}
      <section
        id="overview"
        className="flex flex-col md:flex-row justify-between pt-6  md:pt-32 md:pb-10 bg-white md:gap-20"
      >
        <div className="w-full md:w-1/2 overflow-hidden">
          {/* Ensure ImageMagnifier handles potential missing src gracefully or add check */}
          {product.bannerImageUrl && (
            <ImageMagnifier src={product.bannerImageUrl} zoom={3} />
          )}
        </div>
        <div className="w-full md:w-1/2 md:pl-6 md:pt-0 pt-8 p-4 md:mr-8">
          <h2 className="text-blue-800 font-bold text-lg uppercase mb-4">
            Overview
          </h2>
          <h1 className="text-3xl font-bold mb-4">{product.title}</h1>
          <div className="py-10">
            {/* Check if description is an array */}
            {Array.isArray(product.description)
              ? product.description.map((item, i) => (
                  <p
                    className="text-gray-700 mb-4"
                    key={i}
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))
              : null}
          </div>
          <Link to="/contact">
            <button className="bg-[#cc673d] px-4 py-2 flex items-center hover:bg-[#e27a4d] transition text-white">
              Make An Enquiry
            </button>
          </Link>
        </div>
      </section>

      {/* --- Applications Section --- */}
      {product.madeForList && product.madeForList.length > 0 && (
        <div className="max-w-[1400px] mx-auto py-5 px-4 md:px-0">
          <h2 className="text-blue-800 font-bold text-lg uppercase mb-4">
            Applications
          </h2>
          {product.madeFor && (
            <h2 className="pb-12 text-3xl font-semibold">{product.madeFor}:</h2>
          )}
          <Accordion data={product.madeForList} />
        </div>
      )}

      {/* --- ImageGallery --- */}
      {product.images && product.images.length > 0 && (
        <section id="images" className="md:py-16 py-8">
          <ImageGallery images={product.images} />
        </section>
      )}

      {/* --- Specifications Section --- */}
      {product.specifications &&
        Object.keys(product.specifications).length > 0 && (
          <section id="specifications" className="md:py-16 md:ml-10 ">
            <h2 className="text-3xl font-semibold py-6 px-4 md:px-0">
              Technical Specifications
            </h2>
            <TechnicalSpecifications
              specifications={product.specifications}
              default1={product.specificationsDefault}
              selectedSpec={currentSpec} // Use state variable
              onSpecChange={setCurrentSpec}
            />
          </section>
        )}

      {/* --- MainScrollText --- */}
      {product.mainScrollText && (
        <div>
          <section className="bg-black py-12">
            <MainScrollText data={product.mainScrollText} />
          </section>
        </div>
      )}

      {/* --- FAQ Section --- */}
      {product.faq && product.faqList && product.faqList.length > 0 && (
        <section
          id="faq's"
          className="max-w-[1400px] mx-auto flex h-full py-20 flex-col md:flex-row w-full px-4 md:px-0"
        >
          <div className="md:w-1/2 space-y-8 mb-10 md:mb-0">
            <h1 className="text-4xl font-semibold">Your questions, answered</h1>
            {product.faq.title && (
              <h2 className="text-2xl">{product.faq.title}</h2>
            )}
            {product.faq.link && product.faq.label && (
              <div>
                <Link to={product.faq.link}>
                  <button className="bg-[#cc673d] px-4 py-2 flex items-center hover:bg-[#e27a4d] transition text-white">
                    {product.faq.label}
                  </button>
                </Link>
              </div>
            )}
          </div>
          <div className="md:w-1/2">
            <Accordion data={product.faqList} />
          </div>
        </section>
      )}

      {/* --- Installation Section --- */}
      {product.installation && product.installation.video && (
        <section className="max-w-[1400px] mx-auto h-full md:py-20 ">
          <div className="px-4 md:px-0">
            <h2 className="text-blue-800 font-bold text-lg uppercase mb-4">
              installation
            </h2>
            {product.installation.title && (
              <h1 className="text-4xl font-semibold capitalize">
                {product.installation.title}
              </h1>
            )}
          </div>
          <iframe
            className="w-full md:h-[700px] h-[300px] mt-10"
            src={product.installation.video}
            title="YouTube video player"
            style={{ border: "none" }} // Replaced frameborder
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </section>
      )}

      {/* --- Related ProductSlider --- */}
      {relativeProducts.length > 0 && (
        <div className="overflow-x-hidden">
          <ProductSlider
            data={relativeProducts} // Use state variable
            heading={product.relatedproductHeading || "Related Products"} // Provide default heading
          />
        </div>
      )}

      {/* --- Contact Form Section --- */}
      {product.contactFormInfo && (
        <div id="contact-form">
          <ContactForm
            contactFormData={product.contactFormInfo}
            additionalData={{
              productName: product.title,
              selectedSpecification: currentSpec, // Pass selected spec state
            }}
          />
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
